import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return {
    items,
    pagination,
    totalCount,
    category_count: payload?.category_count,
    payload,
  };
};

// --- Interior designs ---
export const useInteriorDesign = () => {
  const getInteriorDesign = async (
    setInteriorDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
    category_name = "",
  ) => {
    try {
      const params = {};
      if (searchFilter) {
        params.searchFilter = searchFilter;
      }
      if (category_name) {
        params.category_name = category_name;
      }
      const response = await Apis.getInteriorDesign(offset, limit, params);
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      setInteriorDesignData(items);
      setTotal(totalCount);
      return { data: items, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching interior design:", error);
      throw new Error(getApiError(error, "Failed to fetch interior designs."));
    }
  };

  const createInteriorDesign = async (data) => {
    try {
      const response = await Apis.createInteriorDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating interior design:", error);
      throw error;
    }
  };

  const updateInteriorDesign = async (id, data) => {
    try {
      const response = await Apis.updateInteriorDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating interior design:", error);
      throw error;
    }
  };

  const deleteInteriorDesign = async (id) => {
    try {
      const response = await Apis.deleteInteriorDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting interior design:", error);
      throw error;
    }
  };

  const getInteriorDesignById = async (id, setInteriorData) => {
    try {
      const response = await Apis.getInteriorDesignById(id);
      const design = unwrapResponseBody(response?.data);
      if (typeof setInteriorData === "function") setInteriorData(design);
      return design;
    } catch (error) {
      console.error("Error fetching interior design by id:", error);
      throw error;
    }
  };

  const showInteriorDesignOnHomeScreen = async (interiorDesignId, homeScreen) => {
    try {
      const data = {
        interiorDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addInteriorDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateInteriorDesignPosition = async (interiorDesignId, newPosition) => {
    try {
      const response = await Apis.updateInteriorDesignPosition({
        interiorDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating interior design position:", error);
      throw error;
    }
  };

  return {
    getInteriorDesign,
    createInteriorDesign,
    updateInteriorDesign,
    deleteInteriorDesign,
    getInteriorDesignById,
    showInteriorDesignOnHomeScreen,
    updateInteriorDesignPosition,
  };
};

// --- Interior design categories ---
export const useInteriorDesignCategory = () => {
  const addInteriorDesignCategory = async (categoryData) => {
    try {
      const response = await Apis.addInteriorDesignCategory(categoryData);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const getInteriorDesignCategories = async () => {
    try {
      const response = await Apis.getInteriorDesignCategories();
      const { items, pagination, payload } = unwrapListPayload(response?.data);
      return { data: items, pagination, ...payload };
    } catch (error) {
      throw error;
    }
  };

  const getInteriorDesignCategoriesWithPagination = async (
    offset = 0,
    limit = 10,
    name = "",
    status = "",
  ) => {
    try {
      const params = {};
      if (name) {
        params.name = name;
      }
      if (status !== "") {
        params.status =
          status === "true" ? true : status === "false" ? false : status;
      }
      const response = await Apis.getInteriorDesignCategoriesWithPagination(
        offset,
        limit,
        params,
      );
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      return {
        data: items,
        pagination,
        totalCount,
        ...payload,
      };
    } catch (error) {
      throw error;
    }
  };

  const updateInteriorDesignCategory = async (id, categoryData) => {
    try {
      const response = await Apis.updateInteriorDesignCategory(id, categoryData);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const deleteInteriorDesignCategory = async (categoryId) => {
    try {
      const response = await Apis.deleteInteriorDesignCategory(categoryId);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const toggleInteriorDesignCategoryStatus = async (categoryId, status) => {
    try {
      const response = await Apis.toggleInteriorDesignCategoryStatus(
        categoryId,
        status,
      );
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to toggle category status.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const getAllInteriorDesignCategories = async () => {
    try {
      const response = await Apis.getAllInteriorDesignCategories();
      const { items } = unwrapListPayload(response?.data);
      return items;
    } catch (error) {
      throw error;
    }
  };

  const updateInteriorDesignCategoryPosition = async (
    categoryId,
    newPosition,
  ) => {
    try {
      const response = await Apis.updateInteriorDesignCategoryPosition({
        categoryId,
        newPosition,
      });
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update category position.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  return {
    addInteriorDesignCategory,
    getInteriorDesignCategories,
    getInteriorDesignCategoriesWithPagination,
    updateInteriorDesignCategory,
    deleteInteriorDesignCategory,
    toggleInteriorDesignCategoryStatus,
    getAllInteriorDesignCategories,
    updateInteriorDesignCategoryPosition,
  };
};
